import { isNotEmpty } from "utils/validate";
import { getArrayMinMax } from "utils/data";
import { defaultArrowAnnotationTop, defaultGridColor, defaultHoverLabel, defaultPlotConfig, defaultSpikeConfig, defaultTickFont, } from "shared/plotly/defaultConfigs";
import { localizedNumber } from "shared/numberParser";
import { getScoreColor } from "shared/colors";
import { N_A } from "shared/rsDictionary";
import { getTextColor } from "rs-components/ScoreIcon";
import { INSIGHT_INPUT_COLORS } from "store/rs-workspace/utils/scenario.utils";
import * as colors from "styles/colorValues.module.scss";
import * as fonts from "styles/fontValues.module.scss";
const { decimal, group } = localizedNumber.numberSeparators;
export const LINE_LEGEND_WIDTH = 0.071;
const GRAPH_MARKER_PADDING = 0.051;
const INSIGHT_Y_RANGE = [-6.288478452066843, 106.28847845206684];
const getInputColor = (inputId, inputSet) => {
    const sourceInputSetInputIndex = inputSet.inputs.findIndex(({ id }) => id === inputId);
    return INSIGHT_INPUT_COLORS[sourceInputSetInputIndex] ?? colors.black;
};
export const plotConfig = {
    ...defaultPlotConfig,
    showAxisDragHandles: false,
    showAxisRangeEntryBoxes: false,
    scrollZoom: false,
};
const defaultAxisConfig = {
    tickfont: defaultTickFont,
    zeroline: false,
    linewidth: 1,
    tickwidth: 1,
    gridcolor: defaultGridColor,
    linecolor: colors.s900,
    tickformat: "000",
};
export const defaultPlotLayout = {
    autosize: true,
    dragmode: false,
    hovermode: false,
    showlegend: false,
    hoverlabel: defaultHoverLabel,
    margin: { l: 55, r: 0, b: 32, t: 12 },
    separators: `${decimal}${group}`,
};
const getInsightScoreAnnotationConfig = (scoreValue, hasBiasedValues) => {
    const score = isNaN(Number(scoreValue)) ? undefined : Math.round(Number(scoreValue));
    const textColor = getTextColor(score, true, undefined, hasBiasedValues);
    const scoreBgColor = hasBiasedValues ? colors.white : getScoreColor(score);
    return {
        width: 30,
        height: 30,
        text: `<b><span>${score ?? N_A}${hasBiasedValues ? "!" : ""}</span></b>`,
        font: { size: 14, family: fonts.roboto, color: textColor },
        bgcolor: scoreBgColor,
        showarrow: false,
        ...(hasBiasedValues ? { bordercolor: colors.red, borderwidth: 2 } : {}),
    };
};
const insightValueAnnotationErrorConfig = {
    width: 26,
    height: 26,
    text: `<b>!</b>`,
    font: { size: 26, family: fonts.roboto, color: colors.red },
    bgcolor: colors.white,
    bordercolor: colors.red,
    borderwidth: 2,
    showarrow: false,
};
const baseInsightGraphXOffsetShapeConfig = {
    type: "rect",
    line: { width: 0 },
    yref: "paper",
    y0: 0,
    y1: 1,
    fillcolor: "rgba(255, 255, 255, 0.87)",
};
const baseInsightGraphXOffsetShapeLineConfig = {
    type: "line",
    line: { color: colors.black, width: 1 },
    yref: "paper",
    y0: 0,
    y1: 1,
    xref: "x",
};
const getInsightValueShapeAndAnnotation = (insightValue, errored, score, hasBiasedValues, maxXAxisDomain, insightGraphXOffset) => {
    if (!insightValue) {
        return;
    }
    const { isVertical, xValue, yValue } = insightValue;
    const shapes = [];
    let annotation;
    if (isVertical) {
        shapes.push({
            type: "line",
            line: { color: colors.black, width: 2 },
            x0: xValue,
            x1: xValue,
            yref: "paper",
            y1: 1,
            y0: 0,
        });
        annotation = {
            x: xValue,
            y: yValue,
            ...(errored
                ? insightValueAnnotationErrorConfig
                : getInsightScoreAnnotationConfig(score, hasBiasedValues)),
        };
    }
    else {
        shapes.push({
            type: "line",
            line: { color: colors.black, width: 2 },
            y0: yValue,
            y1: yValue,
            xref: insightGraphXOffset ? "x" : "paper",
            x0: insightGraphXOffset?.insightGraphMinMax?.min ?? 0,
            x1: insightGraphXOffset?.insightGraphMinMax?.max ?? maxXAxisDomain,
        });
        annotation = {
            xref: insightGraphXOffset ? "x" : "paper",
            x: insightGraphXOffset?.insightGraphMinMax?.max ?? maxXAxisDomain,
            y: yValue,
            ...(errored
                ? insightValueAnnotationErrorConfig
                : getInsightScoreAnnotationConfig(score, hasBiasedValues)),
        };
        if (insightGraphXOffset) {
            shapes.push({
                ...baseInsightGraphXOffsetShapeConfig,
                xref: "paper",
                x0: 0,
                x1: GRAPH_MARKER_PADDING,
            });
            shapes.push({
                ...baseInsightGraphXOffsetShapeConfig,
                xref: "x",
                x0: insightGraphXOffset?.sourceGraphMinMax?.min,
                x1: insightGraphXOffset?.insightGraphMinMax?.min,
            });
            shapes.push({
                ...baseInsightGraphXOffsetShapeLineConfig,
                x0: insightGraphXOffset.insightGraphMinMax.min,
                x1: insightGraphXOffset.insightGraphMinMax.min,
            });
            shapes.push({
                ...baseInsightGraphXOffsetShapeLineConfig,
                x0: insightGraphXOffset.insightGraphMinMax.max,
                x1: insightGraphXOffset.insightGraphMinMax.max,
            });
            shapes.push({
                ...baseInsightGraphXOffsetShapeConfig,
                xref: "x",
                x0: insightGraphXOffset?.insightGraphMinMax?.max,
                x1: insightGraphXOffset?.sourceGraphMinMax?.max,
            });
            shapes.push({
                ...baseInsightGraphXOffsetShapeConfig,
                xref: "paper",
                x0: maxXAxisDomain - GRAPH_MARKER_PADDING,
                x1: maxXAxisDomain,
            });
        }
    }
    return { annotation, shapes };
};
const getSourceAShapes = ({ sourceAGraph, thresholdValue, withinRangeMin, withinRangeMax }, maxXAxisDomain, inputSet) => {
    if (!sourceAGraph) {
        return;
    }
    const color = getInputColor(sourceAGraph.inputId, inputSet);
    let thresholdShape;
    if (isNotEmpty(thresholdValue)) {
        thresholdShape = {
            type: "line",
            line: { color: color, width: 2, dash: "dot" },
            yref: "y2",
            y0: thresholdValue,
            y1: thresholdValue,
            xref: "paper",
            x0: 0,
            x1: maxXAxisDomain,
            layer: "below",
        };
    }
    let withinRangeShape;
    if (isNotEmpty(withinRangeMin) && isNotEmpty(withinRangeMax)) {
        withinRangeShape = {
            type: "rect",
            line: { width: 0 },
            yref: "y2",
            y0: withinRangeMin,
            y1: withinRangeMax,
            fillcolor: color,
            opacity: 0.3,
            xref: "paper",
            x0: 0,
            x1: maxXAxisDomain,
            layer: "below",
        };
    }
    return [...(thresholdShape ? [thresholdShape] : []), ...(withinRangeShape ? [withinRangeShape] : [])];
};
const getInsightGraphXOffset = ({ insightGraph, sourceAGraph, sourceBGraph }) => {
    const insightGraphMinMax = getArrayMinMax(insightGraph?.xValues ?? insightGraph?.spreadXValues ?? []);
    const sourceGraphMinMax = getArrayMinMax(sourceAGraph?.xValues ??
        sourceAGraph?.spreadXValues ??
        sourceBGraph?.xValues ??
        sourceBGraph?.spreadXValues ??
        []);
    return insightGraphMinMax.min > sourceGraphMinMax.min || insightGraphMinMax.max < sourceGraphMinMax.max
        ? { insightGraphMinMax, sourceGraphMinMax }
        : undefined;
};
const getYAxesConfigs = (lines, maxXAxisDomain) => lines.reduce((acc, { inputId }, index) => {
    const isMainLine = inputId === -1;
    const axisConfig = {
        ...defaultAxisConfig,
        tickangle: isMainLine ? -45 : 45,
        tickcolor: colors.black,
        linecolor: colors.black,
        tickfont: { ...defaultTickFont, color: colors.black },
        ...(lines.length - 1 !== index ? { overlaying: "y2" } : {}),
        ...(isMainLine
            ? { range: INSIGHT_Y_RANGE }
            : { anchor: "free", position: maxXAxisDomain, side: "right" }),
    };
    return {
        ...acc,
        [`yaxis${index === 0 ? "" : 2}`]: axisConfig,
    };
}, {});
export const getPlotLayout = (graphShouldBeEmpty, errored, hasBiasedValues, score, graphResult, inputSet) => {
    if (graphShouldBeEmpty || !graphResult || !inputSet) {
        return defaultPlotLayout;
    }
    const { value, sourceAGraph, sourceBGraph, insightGraph } = graphResult;
    const lines = [insightGraph ?? { inputId: -1 }, sourceAGraph, sourceBGraph].filter(Boolean);
    const maxXAxisDomain = 1 - (lines.length > 1 ? LINE_LEGEND_WIDTH : 0);
    const insightGraphXOffset = getInsightGraphXOffset(graphResult);
    const { annotation: insightValueAnnotation, shapes: insightValueShapes } = getInsightValueShapeAndAnnotation(value, errored, score, hasBiasedValues, maxXAxisDomain, insightGraphXOffset) ?? {};
    const sourceAShapes = getSourceAShapes(graphResult, maxXAxisDomain, inputSet);
    return {
        ...defaultPlotLayout,
        xaxis: { ...defaultAxisConfig, ...defaultSpikeConfig, domain: [0, maxXAxisDomain] },
        ...getYAxesConfigs(lines, maxXAxisDomain),
        shapes: [
            ...(insightValueShapes?.length ? [...insightValueShapes] : []),
            ...(sourceAShapes?.length ? [...sourceAShapes] : []),
        ],
        annotations: [
            { ...defaultArrowAnnotationTop, xshift: -0.3 },
            ...(insightValueAnnotation ? [insightValueAnnotation] : []),
        ],
    };
};
const prepareLineSpread = (sourceValues, fillcolor, yaxis) => ({
    x: sourceValues.spreadXValues,
    y: sourceValues.spreadYValues,
    type: "scatter",
    mode: "lines+markers",
    line: { color: "transparent" },
    marker: { opacity: 0, size: 4 },
    fill: "toself",
    fillcolor: fillcolor,
    opacity: 0.3,
    yaxis: yaxis,
});
const prepareLine = (sourceValues, line, yaxis) => ({
    x: sourceValues.xValues,
    y: sourceValues.yValues,
    type: "scatter",
    mode: "lines+markers",
    line: { width: 3, ...line },
    marker: { opacity: 0, size: 4 },
    yaxis: yaxis,
});
export const getPlotData = (graphShouldBeEmpty, graphResult, inputSet) => {
    if (graphShouldBeEmpty || !graphResult || !inputSet) {
        return [];
    }
    const { insightGraph, sourceAGraph, sourceBGraph } = graphResult;
    const lines = [insightGraph ?? { yValues: [], xValues: [] }, sourceAGraph, sourceBGraph].filter(Boolean);
    return lines.reduce((acc, values, index) => {
        const { inputId, xValues, yValues, spreadYValues, spreadXValues } = values;
        const yaxis = `y${index === 0 ? "" : 2}`;
        const line = inputId === -1
            ? { color: colors.s600, dash: "dash" }
            : { color: getInputColor(inputId, inputSet) };
        return [
            ...acc,
            ...(xValues && yValues ? [prepareLine(values, line, yaxis)] : []),
            ...(spreadXValues && spreadYValues ? [prepareLineSpread(values, line.color, yaxis)] : []),
        ];
    }, []);
};
